import { createReferralsPageView } from '@spotify-internal/event-definitions/es5/events/createReferralsPageView';
import { createReferralsPageViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createReferralsPageViewNonAuth';
import {
  createReferralPageInteraction,
  ReferralPageInteractionEventData,
} from '@spotify-internal/event-definitions/es5/events/createReferralPageInteraction';
import { createReferralPageInteractionNonAuth } from '@spotify-internal/event-definitions/es5/events/createReferralPageInteractionNonAuth';
import { GabitoEventDefinition, EventDefinition, Tracker } from './tracking';
import { EventParams } from '../../server/types';

export type GabitoEventDefinitionsWithAuth =
  GabitoEventDefinition['options'] & { authorize: boolean };

export type ReferralPageViewEventParams = {
  page: string;
  market: string;
  http_referer?: string;
};

export type ReferralPageInteractionEventParams = {
  page: string;
  market: string;
  referralCode: string;
};

export const shareButtonClickEvent = (
  eventData: ReferralPageInteractionEventParams,
): EventDefinition[] => [
  {
    tracker: Tracker.DataLayer,
    eventData: {
      event: 'createReferralCTA',
    },
  },
  {
    tracker: Tracker.Gabito,
    eventData: createReferralPageInteraction({
      page: eventData.page,
      market: eventData.market,
      interaction: 'SHARE_CTA_CLICKED',
      properties: {
        referral_code: eventData.referralCode,
      },
    }),
    options: {
      authorize: true,
    },
  },
];

export const productClickEvent = (
  params: Omit<ReferralPageInteractionEventParams, 'page' | 'interaction'> & {
    eventParams?: EventParams;
    component?: string;
    position?: number | null;
    componentType?: string;
    link?: string | null;
  },
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => {
  const eventData = {
    page: 'referrals',
    market: params.market,
    interaction: 'PRODUCT_CTA_CLICKED',
    properties: {
      'campaign-id': params.eventParams?.campaignId ?? '',
      'offer-type-id': params.eventParams?.offerTypeId ?? '',
      component: params.component ?? '',
      position: Number.isInteger(params.position)
        ? params.position!.toString()
        : '',
      href: params.link ?? '',
      referral_code: params.referralCode,
    },
  } as ReferralPageInteractionEventData;
  return [
    {
      tracker: Tracker.DataLayer,
      eventData: {
        event: 'productClick',
        ecommerce: {
          click: {
            products: [
              {
                country_code: eventData.market,
                variant: eventData.properties?.['campaign-id'],
                name: eventData.properties?.['offer-type-id'],
                list: eventData.properties?.component,
                position: eventData.properties?.position,
                href: eventData.properties?.href,
              },
            ],
          },
        },
      },
    },
    {
      tracker: Tracker.Gabito,
      eventData: options.authorize
        ? createReferralPageInteraction(eventData)
        : createReferralPageInteractionNonAuth(eventData),
      options,
    },
  ];
};

export const referralPageViewEvent = (
  eventData: ReferralPageViewEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createReferralsPageView({
          page: eventData.page,
          market: eventData.market,
          http_referer: eventData.http_referer,
        })
      : createReferralsPageViewNonAuth({
          page: eventData.page,
          market: eventData.market,
        }),
    options,
  },
];

export const errorPageViewEvent = (
  eventData: ReferralPageViewEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.DataLayer,
    eventData: {
      event: 'referralError',
      eventLabel: eventData.page,
    },
  },
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createReferralsPageView({
          page: eventData.page,
          market: eventData.market,
        })
      : createReferralsPageViewNonAuth({
          page: eventData.page,
          market: eventData.market,
        }),
    options,
  },
];
